import createNestedObject from './createNestedObject';
import getDotNotationFromString from './getDotNotationFromString';
import slugMap from './slugMap';

/**
 *
 * @param {Object} prevDataConfig The prev object from the project configuration
 * @param {string} project The project name from ws-tagging
 * @param {string} grouping The grouping name from ws-tagging
 * @param {Object} window The window object
 * @description Method will loop through prevDataConfig and save any requested items to localStorage
 */
const writePrevData = (prevDataConfig, project, grouping, window) => {
	const prev = {};

	// [slug,] looks funky and it's because we don't need the second argument yet
	Object.entries(prevDataConfig).forEach(([slug]) => {
		const slugPath = slugMap[slug];
		const item = getDotNotationFromString(window.DDC, slugPath);
		if (item) {
			createNestedObject(prev, slugPath.replace('dataLayer.', ''), item);
		}
	});

	if (Object.keys(prev).length > 0) {
		// writing this out project/grouping specific to:
		// - avoid future collisions of multiple tracking projects on one site
		window.localStorage.setItem(
			`wsTaggingPrev|${project}|${grouping}`,
			JSON.stringify(prev)
		);
	} else {
		window.localStorage.removeItem(`wsTaggingPrev|${project}|${grouping}`);
	}
};

/**
 *
 * @param {string} project The project name from ws-tagging
 * @param {string} grouping The grouping name from ws-tagging
 * @param {Object} window The window object
 * @returns An object of data from the previous page
 */
export const getPreviousData = (project, grouping, window) => {
	return JSON.parse(
		window.localStorage.getItem(`wsTaggingPrev|${project}|${grouping}`)
	);
};

/**
 *
 * @param {Object} prevDataConfig The prev object from the project configuration
 * @param {string} project The project name from ws-tagging
 * @param {string} grouping The grouping name from ws-tagging
 * @param {Object} window The window object
 * @description Method facilitates populating a localStorage property with data to be used on the next page load
 */
export const saveDDCDataLayer = (
	prevDataConfig = {},
	project,
	grouping,
	window
) => {
	// initial pass of window.DDC.dataLayer from page load
	writePrevData(prevDataConfig, project, grouping, window);

	if (window?.DDC?.APILoader) {
		// window.DDC.dataLayer is populated with more data post page load
		// window.DDC.dataLayer.page.attributes.vehicleResultCount is null until wiapi vehicle-data-updated-v1 triggers
		(async (APILoader) => {
			const API = await APILoader.create('ws-tagging previousData.js');
			// may need what we subscribe to to be configurable in the future
			API.subscribe('vehicle-data-updated-v1', () => {
				// second call repopulates data now that vehicle data has been updated
				writePrevData(prevDataConfig, project, grouping, window);
			});
		})(window.DDC.APILoader);
	}
};

export const savePrevEventData = (
	prevDataConfigEvents = {},
	project,
	grouping,
	event,
	eventData,
	win
) => {
	const localStorageKey = `wsTaggingPrevEvents${project}${grouping}`;
	const prevEventData =
		JSON.parse(win.localStorage.getItem(localStorageKey)) || {};
	let updated = false;
	Object.entries(prevDataConfigEvents).forEach(([eventName, properties]) => {
		if (eventName === event) {
			properties.forEach((property) => {
				if (eventData?.[property]) {
					const eventNameNoPeriod = eventName.replaceAll('.', '');
					prevEventData[eventNameNoPeriod] =
						prevEventData[eventNameNoPeriod] || {};
					prevEventData[eventNameNoPeriod][property] =
						eventData[property];
					updated = true;
				}
			});
		}
	});
	if (updated) {
		localStorage.setItem(localStorageKey, JSON.stringify(prevEventData));
	}
	return prevEventData;
};
